import { useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, ScanBarcode, ScanLine, ShieldCheck, WifiOff } from 'lucide-react'
import { QrScanner, tokenFromScan } from '@/components/QrScanner'
import { badgeTokenFrom, employeeCodeFrom } from '@/lib/badgeScan'
import { useScannedInput } from '@/lib/useScannedInput'
import { useAuth } from '@/context/AuthContext'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { clearRememberedRoute, getRememberedRoute } from '@/lib/lastRoute'
import { Button, Checkbox, Field, Input, PasswordInput } from '@/components/ui'
import { KaproLogo } from '@/components/Logo'
import { AuthLayout } from '@/components/AuthLayout'
import { useT } from '@/i18n/I18nProvider'

/**
 * Sign in by holding a printed badge up to the camera.
 *
 * The token in the card is a bearer credential — whoever holds the card is that
 * worker — so it is spent immediately and never kept in component state longer
 * than the call. A failed scan says so and offers the form back rather than
 * silently retrying, because a damaged card needs a supervisor, not patience.
 */
function BadgeScanStep({
  remember,
  onScanned,
  onCancel,
}: {
  remember: boolean
  onScanned: (pending: { twoFactorRequired: true; challenge: string } | null) => void
  onCancel: () => void
}) {
  const { loginWithBadge } = useAuth()
  const t = useT()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  return (
    <div className="mt-8 space-y-4">
      {!error && !busy && (
        <QrScanner
          onError={setError}
          onResult={(text) => {
            setBusy(true)
            loginWithBadge(tokenFromScan(text), remember)
              .then(onScanned)
              .catch((err) => {
                setError(
                  err instanceof ApiError
                    ? (err.fieldError('token') ?? err.message)
                    : t('login.badge.failed'),
                )
              })
              .finally(() => setBusy(false))
          }}
        />
      )}

      {busy && <p className="muted text-center text-sm">{t('login.badge.working')}</p>}

      {error && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300"
        >
          {error}
        </p>
      )}

      <p className="muted text-center text-sm">{t('login.badge.hint')}</p>

      <Button variant="secondary" fullWidth onClick={onCancel}>
        {t('login.badge.back')}
      </Button>
    </div>
  )
}

/**
 * The second step, shown once the password has been accepted but no session
 * exists yet.
 *
 * The recovery-code path is offered here rather than hidden behind a support
 * request, because for an administrator there is nobody else to ask: their own
 * account is the one that resets everyone else's.
 */
function TwoFactorStep({
  challenge,
  remember,
  onCancel,
  onDone,
}: {
  challenge: string
  remember: boolean
  onCancel: () => void
  onDone: () => void
}) {
  const { completeTwoFactor } = useAuth()
  const t = useT()

  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      await completeTwoFactor(
        challenge,
        useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() },
        remember,
      )
      onDone()
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.fieldError('code') ?? err.fieldError('recoveryCode') ?? err.message)
          : t('login.twoFactor.failed'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
      <div className="flex items-center gap-2.5 rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200">
        <ShieldCheck className="size-4 shrink-0" aria-hidden />
        <span>{t('login.twoFactor.accepted')}</span>
      </div>

      <Field
        label={t(useRecovery ? 'login.twoFactor.recoveryCode' : 'login.twoFactor.appCode')}
        required
        hint={t(
          useRecovery
            ? 'login.twoFactor.recoveryCode.hint'
            : 'login.twoFactor.appCode.hint',
        )}
      >
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          // A rotating code must never be autofilled from a saved password.
          autoComplete={useRecovery ? 'off' : 'one-time-code'}
          inputMode={useRecovery ? 'text' : 'numeric'}
          placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
          enterKeyHint="go"
          required
          autoFocus
          className={cn(!useRecovery && 'text-center text-lg tracking-[0.3em] tabular-nums')}
          aria-invalid={error ? true : undefined}
        />
      </Field>

      {error && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300"
        >
          {error}
        </p>
      )}

      <Button type="submit" fullWidth loading={submitting}>
        {t('login.twoFactor.confirm')}
      </Button>

      <div className="flex items-center justify-between gap-3 text-sm">
        <button
          type="button"
          onClick={() => {
            setUseRecovery((current) => !current)
            setCode('')
            setError(null)
          }}
          className="text-brand-600 min-h-11 font-medium"
        >
          {t(useRecovery ? 'login.twoFactor.haveAppCode' : 'login.twoFactor.lostPhone')}
        </button>

        <button type="button" onClick={onCancel} className="muted min-h-11">
          {t('login.twoFactor.back')}
        </button>
      </div>
    </form>
  )
}

/*
 * The filled field style this screen used to define for itself now lives in
 * form.tsx as the default for every control, so there is nothing to add here.
 *
 * Worth restating, because it is the trap this style invites: the labels above
 * these boxes are real `<label for>` elements and stay visible.
 * Placeholder-as-label is how the look is usually built, and the label vanishes
 * the moment someone types — so anyone interrupted mid-form, which at a machine
 * is most people, comes back to two identical grey boxes.
 */

export function LoginPage() {
  const { user, login, loginWithBadge, isLoading } = useAuth()
  const t = useT()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  /*
   * Where to go after signing in.
   *
   * Set when a worker scans a QR sticker on a phone whose session has lapsed —
   * they should end up at the form for that machine, not back at the home page
   * having to find it again.
   *
   * Only same-site paths are honoured. Accepting an arbitrary value here would
   * turn the login page into an open redirect.
   */
  const rawNext = searchParams.get('nastavi')
  const explicitNext = rawNext?.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null

  /*
   * Failing that, the screen they were last on.
   *
   * Only ever set for a remembered session — see lib/lastRoute.ts — so on a
   * shared terminal this is always null and everyone starts at the home screen.
   */
  const next = explicitNext ?? getRememberedRoute() ?? '/'

  /*
   * Prefilled after a password reset, which redirects here rather than signing
   * the user in — a session minted from a mailbox would walk straight past any
   * second factor. The code comes back so the only thing left to type is the
   * password they just chose.
   */
  const [employeeCode, setEmployeeCode] = useState(() => searchParams.get('sifra') ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  /*
   * The error was a refusal, not a wrong password.
   *
   * A 403 here means the credentials were right and the door is shut anyway —
   * the company is suspended, or it does not allow this way of signing in.
   * The server's sentence says which, with the reason the owner gave, and it
   * is shown exactly as sent. What changes is the frame around it: amber with
   * a lock rather than red, and the password is not selected for retyping,
   * because retyping it is the one thing that cannot help.
   */
  const [refused, setRefused] = useState(false)

  /*
   * On by default, which is what a worker on their own phone wants and what the
   * app did before this existed. The people who need it off are the ones
   * signing in on a shared shop-floor terminal, and they are the ones who will
   * notice the checkbox.
   */
  const [remember, setRemember] = useState(true)

  /** Set when the account has 2FA — swaps the form for the code step. */
  const [challenge, setChallenge] = useState<string | null>(null)

  /** Swaps the form for the camera, to sign in with a printed badge. */
  const [scanning, setScanning] = useState(false)

  /** A card went through the reader and the sign-in is in flight. */
  const [reading, setReading] = useState(false)

  /** A worker card filled the code box, so the line below can say so. */
  const [scannedCode, setScannedCode] = useState(false)

  const passwordRef = useRef<HTMLInputElement>(null)

  /*
   * First-time sign-in, which is the same request with a different label.
   *
   * The activation code *is* the password — `issueActivationCode()` sets it as
   * one and `/auth/login` checks it as one — so there is no second endpoint and
   * no second form. What was missing was any sign of that on this screen:
   * somebody holding a slip that reads "Aktivacioni kod" has no reason to type
   * it into a field called "Lozinka", which is why accounts imported from the
   * MES looked impossible to enter.
   */
  const [firstTime, setFirstTime] = useState(false)

  /*
   * The other other way in: a badge held under a scanner wired to this
   * terminal.
   *
   * Nothing to press and nothing to focus — see `useBadgeScanner`. Switched off
   * whenever this screen is showing something else, because a burst of
   * keystrokes arriving during the camera step or the two-factor code would be
   * answering a question nobody asked.
   */
  useScannedInput(handleScan, {
    enabled: !scanning && !challenge && !submitting && !reading,
  })

  if (isLoading) return null
  if (user) return <Navigate to={next} replace />

  /**
   * Something was scanned. Work out which of the two cards it was.
   *
   * A **KaproLean badge** carries a bearer token and is a whole sign-in on its
   * own. A **worker's existing MES card** carries their `barkod`, which is this
   * application's `employee_code` — a username, printed on the card they are
   * already wearing, and the way Livprom's people have signed in for years. The
   * second is far more common on that floor today, and a login screen that read
   * only the first would ignore every card in the plant.
   *
   * Anything else is left alone entirely.
   */
  function handleScan(text: string) {
    const token = badgeTokenFrom(text)

    if (token) {
      void signInWithBadge(token)
      return
    }

    const code = employeeCodeFrom(text)

    if (!code) return

    /*
     * A username in a username box: nothing is committed and the password is
     * still required, so this is the one branch that can be generous.
     *
     * The password is cleared because a scan means a *different person* has
     * stepped up to the terminal — leaving the last one's half-typed password
     * under a new name is both confusing and wrong.
     */
    setEmployeeCode(code)
    setPassword('')
    setError(null)
    setScannedCode(true)
    passwordRef.current?.focus()
  }

  /**
   * A badge was read. Spend the token and get out of the way.
   *
   * The fields are cleared first, and that is not tidiness. A wedge types into
   * whatever has focus, so the token — a bearer credential, the whole secret on
   * the card — has just been typed into the employee-code box in plain sight on
   * a screen by a machine. It goes before anything else happens.
   */
  async function signInWithBadge(token: string) {
    setEmployeeCode('')
    setPassword('')
    setError(null)
    setRefused(false)
    setReading(true)

    try {
      const pending = await loginWithBadge(token, remember)

      if (pending) {
        // A badge is one factor. An account carrying a second still has to
        // answer for it.
        setChallenge(pending.challenge)
        return
      }

      if (!remember) clearRememberedRoute()

      navigate(explicitNext ?? (remember ? getRememberedRoute() : null) ?? '/', { replace: true })
    } catch (err) {
      setRefused(err instanceof ApiError && err.status === 403)
      setError(
        err instanceof ApiError
          ? (err.fieldError('token') ?? err.message)
          : t('login.badge.failed'),
      )
    } finally {
      setReading(false)
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setRefused(false)
    setSubmitting(true)

    try {
      const pending = await login(employeeCode.trim(), password, remember)

      // The account has a second factor: no session yet, so swap the form for
      // the code step rather than navigating.
      if (pending) {
        setChallenge(pending.challenge)
        return
      }

      /*
       * An explicit `nastavi` always wins — it means a scanned sticker or a
       * session that lapsed on a specific page. Otherwise, resume where they
       * left off, but only if they asked to be remembered; unchecking the box
       * also means "leave no trail on this device", so the crumb is dropped.
       */
      if (!remember) clearRememberedRoute()

      const destination = explicitNext ?? (remember ? getRememberedRoute() : null) ?? '/'

      navigate(destination, { replace: true })
    } catch (err) {
      const refusal = err instanceof ApiError && err.status === 403

      setRefused(refusal)
      setError(
        err instanceof ApiError
          ? (err.fieldError('employeeCode') ?? err.message)
          : t('login.failed'),
      )

      // Right password, closed door — see `refused` above.
      if (refusal) return

      /*
       * Put the cursor back in the password box with the old value selected, so
       * the next attempt is one keystroke away.
       *
       * Almost every failure here is a mistyped password rather than a wrong
       * employee code — the code is printed on the worker's badge, the password
       * was read out to them by a supervisor. Leaving them to tap into the right
       * field and clear it by hand, on a phone, is the difference between
       * retrying and giving up.
       */
      passwordRef.current?.focus()
      passwordRef.current?.select()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <>
            <div className="flex flex-col items-center text-center">
              <KaproLogo size="lg" />
              {/* Hidden on a short phone. It says what the app is to somebody
                  who already opened it to sign in — the first thing to go when
                  the alternative is scrolling to reach the button. */}
              <p className="muted mt-3 hidden text-sm [@media(min-height:700px)]:block">
                {t('login.tagline')}
              </p>
            </div>

            {/*
              noValidate: the browser's own "Please fill out this field" bubble
              is in the browser's UI language rather than the one this screen is
              set to — which is now a choice somebody made, so the two disagree
              more often than they used to — and it points at a field the worker
              can already see is empty. `required` is kept for the semantics and
              for assistive tech.
            */}
            {scanning ? (
              <BadgeScanStep
                onCancel={() => setScanning(false)}
                onScanned={(pending) => {
                  setScanning(false)
                  if (pending) setChallenge(pending.challenge)
                  else navigate(explicitNext ?? getRememberedRoute() ?? '/', { replace: true })
                }}
                remember={remember}
              />
            ) : challenge ? (
              <TwoFactorStep
                challenge={challenge}
                remember={remember}
                onCancel={() => setChallenge(null)}
                onDone={() => {
                  if (!remember) clearRememberedRoute()
                  navigate(
                    explicitNext ?? (remember ? getRememberedRoute() : null) ?? '/',
                    { replace: true },
                  )
                }}
              />
            ) : (
              <>
            {/* The height queries here and below trim whitespace, never
                content, and only on a viewport too short to afford it — a
                375×667 phone is the one this app still has to fit. */}
            <form
              onSubmit={handleSubmit}
              className="mt-8 space-y-4 [@media(max-height:700px)]:mt-5"
              noValidate
            >
              <Field label={t('login.employeeCode')} required>
                <Input
                  name="employeeCode"
                  value={employeeCode}
                  onChange={(e) => {
                    setEmployeeCode(e.target.value)
                    setScannedCode(false)
                  }}
                  placeholder={t('login.employeeCode.placeholder')}
                  autoComplete="username"
                  // Workers type this on a phone. No autocapitalise, no
                  // autocorrect — both mangle a code like "emp-1042".
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  // Sends the phone keyboard's action key to the password field
                  // rather than submitting a half-filled form.
                  enterKeyHint="next"
                  required
                  // Skipped when the code arrived prefilled from a reset —
                  // focusing a field that is already correct just makes the
                  // person tab past it.
                  autoFocus={!employeeCode}
                  // Neither field is singled out: the server rejects the pair
                  // without saying which half was wrong, and guessing would
                  // point at the wrong box half the time.
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </Field>

              <Field
                label={firstTime ? t('login.activationCode') : t('login.password')}
                required
                hint={firstTime ? t('login.activationCode.hint') : undefined}
                /*
                  On the label's line, not below the field. A link, and only
                  since recovery by e-mail was built — for a long time this was
                  a sentence rather than a link, on the grounds that most
                  workers have no e-mail address and a dead end is worse than an
                  honest instruction. The screen behind it now explains which
                  route applies to you before you commit to waiting on a mailbox
                  you may not have.

                  Hidden while entering an activation code: somebody who has
                  never had a password cannot have forgotten one, and the route
                  behind it needs an e-mail address they are the least likely of
                  anybody to have.
                */
                action={
                  firstTime ? undefined : (
                    <Link
                      to="/prijava/zaboravljena-lozinka"
                      className="muted text-xs underline-offset-4 hover:underline"
                    >
                      {t('login.forgotPassword')}
                    </Link>
                  )
                }
              >
                <PasswordInput
                  ref={passwordRef}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  /*
                    `one-time-code` rather than `current-password` in this mode.
                    Offering to save an activation code to a password manager,
                    or filling it from one, is offering to keep a credential
                    that stops working the moment it is used.
                  */
                  autoComplete={firstTime ? 'one-time-code' : 'current-password'}
                  enterKeyHint="go"
                  required
                  autoFocus={!!employeeCode}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </Field>

              {/*
                The way into first-time mode, under the field it changes rather
                than beside the label — the label row already carries the
                forgotten-password link, and two links there read as a choice
                between equals when one of them applies to almost nobody.
              */}
              <button
                type="button"
                onClick={() => setFirstTime((it) => !it)}
                className="muted -mt-1 self-start text-xs underline-offset-4 hover:underline"
              >
                {firstTime
                  ? t('login.firstTime.back')
                  : `${t('login.firstTime')} ${t('login.firstTime.switch')}`}
              </button>

              <Checkbox
                name="remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                label={t('login.remember')}
                hint={t('login.remember.hint')}
              />

              {error && (
                <p
                  id="login-error"
                  role="alert"
                  className={cn(
                    'flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm',
                    refused
                      ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/60 dark:text-amber-100'
                      : 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
                  )}
                >
                  {refused && <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />}
                  <span>{error}</span>
                </p>
              )}

              <Button type="submit" fullWidth loading={submitting}>
                {t('login.submit')}
              </Button>
            </form>

            {/*
              The other way in.

              For most workers this is the primary one — they point their
              phone's camera at the card and the app opens already signed in,
              never reaching this screen at all. The button is here for the
              shared terminal by the door, which has a webcam and no card
              reader.
            */}
            <div className="mt-6 [@media(max-height:700px)]:mt-4">
              <div className="flex items-center gap-3">
                <span className="bg-border h-px flex-1" />
                <span className="muted text-xs">{t('login.or')}</span>
                <span className="bg-border h-px flex-1" />
              </div>

              <Button
                variant="secondary"
                fullWidth
                className="mt-4"
                onClick={() => setScanning(true)}
                disabled={reading}
              >
                <ScanLine className="size-4" aria-hidden />
                {t('login.scanBadge')}
              </Button>

              {/*
                Said out loud, because a feature with no control is a feature
                nobody discovers. There is nothing to press — the reader types
                and the page answers — so the only way anybody learns it is
                there is a sentence saying so.

                `aria-live`, so the same line can report the scan in progress to
                a reader that is not watching the screen.
              */}
              <p
                className="muted mt-3 flex items-start gap-2 text-xs"
                aria-live="polite"
              >
                <ScanBarcode className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  {t(
                    reading
                      ? 'login.reader.working'
                      : scannedCode
                        ? 'login.reader.codeRead'
                        : 'login.reader.hint',
                  )}
                </span>
              </p>
            </div>

            {/*
              The activation-code route used to be spelled out here. It moved
              behind "Zaboravili ste lozinku?", which is where somebody who
              needs it is already going, and taking it off this screen is what
              got the sign-in button back above the fold.
            */}
              </>
            )}

            <div className="bg-border my-4 h-px [@media(max-height:700px)]:my-2" />

            <p className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
              <WifiOff className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{t('login.offlineNotice')}</span>
            </p>
      </>
    </AuthLayout>
  )
}
